<template>
  <div class="settings columns container">
    <woot-loading-state
      v-if="isLoading"
      :message="$t('INTEGRATION_SETTINGS.ASSISTENT.LOADING')"
    />

    <empty-state
      v-else-if="!assistent.id"
      :title="$t('INTEGRATION_SETTINGS.ASSISTENT.NOT_FOUND.TITLE')"
      :message="$t('INTEGRATION_SETTINGS.ASSISTENT.NOT_FOUND.MESSAGE')"
    >
      <router-link class="button" :to="{ name: 'virtual_assistants_list' }">
        {{ $t('INTEGRATION_SETTINGS.ASSISTENT.NOT_FOUND.BACK_BUTTON') }}
      </router-link>
    </empty-state>

    <template v-else>
      <setting-intro-banner :header-title="assistent.name">
        <div ref="tabsWrapper" class="settings--tabs-wrapper">
          <woot-tabs
            class="settings--tabs"
            :index="mainTabsIndex"
            :border="false"
            @change="onMainTabChange"
          >
            <woot-tabs-item
              v-for="tab in mainTabs"
              :key="tab.key"
              :name="tab.name"
              :show-badge="false"
            />
            <woot-tabs-item
              ref="extraButton"
              :name="$t('INTEGRATION_SETTINGS.ASSISTENT.TABS.MORE')"
              :show-badge="false"
              :chevron="true"
              :is-open="showExtraMenu"
              class="extra-tabs-trigger"
              @click.native.stop="toggleExtraMenu"
            />
          </woot-tabs>
          <ul
            v-show="showExtraMenu"
            ref="extraMenu"
            class="extra-tabs__menu"
            :style="extraMenuStyle"
          >
            <li
              v-for="tab in extraTabs"
              :key="tab.key"
              class="extra-tabs__item"
              @click="selectExtraTab(tab)"
            >
              {{ tab.name }}
            </li>
          </ul>
        </div>
      </setting-intro-banner>

      <div class="settings--content">
        <div v-if="selectedTabKey === 'general'">
          <general-settings-tab :assistent="assistent" />
        </div>
        <div v-if="selectedTabKey === 'instructions'">
          <instructions-tab :assistent="assistent" />
        </div>
        <div v-if="selectedTabKey === 'knowledge_base'">
          <knowledge-base-tab :assistent="assistent" />
        </div>
        <div v-if="selectedTabKey === 'public_knowledge_base'">
          <public-knowledge-base-tab :assistent="assistent" />
        </div>
        <div v-if="selectedTabKey === 'faq'">
          <faq-tab :assistent="assistent" />
        </div>
        <div v-if="selectedTabKey === 'labels'">
          <labels-tab :assistent="assistent" />
        </div>
        <div v-if="selectedTabKey === 'teams'">
          <teams-tab :assistent="assistent" />
        </div>
        <div v-if="selectedTabKey === 'handoff'">
          <handoff-rules-tab :assistent="assistent" />
        </div>
        <div v-if="selectedTabKey === 'internet_access'">
          <internet-access-tab :assistent="assistent" />
        </div>
        <div v-if="selectedTabKey === 'macros'">
          <macros-tab :assistent="assistent" />
        </div>
        <div v-if="selectedTabKey === 'resolution_rules'">
          <resolution-rules-tab :assistent="assistent" />
        </div>
        <div v-if="selectedTabKey === 'resume'">
          <resume-tab :assistent="assistent" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import SettingIntroBanner from 'dashboard/components/widgets/SettingIntroBanner';
import EmptyState from 'dashboard/components/widgets/EmptyState';
import GeneralSettingsTab from './components/tabs/GeneralSettingsTab';
import InstructionsTab from './components/tabs/InstructionsTab';
import KnowledgeBaseTab from './components/tabs/KnowledgeBaseTab';
import PublicKnowledgeBaseTab from './components/tabs/PublicKnowledgeBaseTab';
import FaqTab from './components/tabs/FaqTab';
import LabelsTab from './components/tabs/LabelsTab';
import TeamsTab from './components/tabs/TeamsTab';
import HandoffRulesTab from './components/tabs/HandoffRulesTab';
import InternetAccessTab from './components/tabs/InternetAccessTab';
import MacrosTab from './components/tabs/MacrosTab';
import ResolutionRulesTab from './components/tabs/ResolutionRulesTab';
import ResumeTab from './components/tabs/ResumeTab';

export default {
  components: {
    SettingIntroBanner,
    EmptyState,
    GeneralSettingsTab,
    InstructionsTab,
    KnowledgeBaseTab,
    PublicKnowledgeBaseTab,
    FaqTab,
    LabelsTab,
    TeamsTab,
    HandoffRulesTab,
    InternetAccessTab,
    MacrosTab,
    ResolutionRulesTab,
    ResumeTab,
  },
  data() {
    return {
      selectedTabIndex: 0,
      showExtraMenu: false,
      extraMenuStyle: {},
      isLoading: false,
    };
  },
  computed: {
    currentAssistentId() {
      return this.$route.params.assistentId;
    },
    assistent() {
      return (
        this.$store.getters['assistent/getAssistent'](
          this.currentAssistentId
        ) || {}
      );
    },
    tabs() {
      return [
        {
          key: 'general',
          name: this.$t('INTEGRATION_SETTINGS.ASSISTENT.TABS.SETTINGS'),
        },
        {
          key: 'instructions',
          name: this.$t('INTEGRATION_SETTINGS.ASSISTENT.TABS.INSTRUCTIONS'),
        },
        {
          key: 'knowledge_base',
          name: this.$t('INTEGRATION_SETTINGS.ASSISTENT.TABS.KNOWLEDGE_BASE'),
        },
        {
          key: 'public_knowledge_base',
          name: this.$t(
            'INTEGRATION_SETTINGS.ASSISTENT.TABS.PUBLIC_KNOWLEDGE_BASE'
          ),
        },
        {
          key: 'faq',
          name: this.$t('INTEGRATION_SETTINGS.ASSISTENT.TABS.FAQ'),
        },
        {
          key: 'labels',
          name: this.$t('INTEGRATION_SETTINGS.ASSISTENT.TABS.LABELS'),
        },
        {
          key: 'teams',
          name: this.$t('INTEGRATION_SETTINGS.ASSISTENT.TABS.TEAMS'),
        },
        {
          key: 'handoff',
          name: this.$t('INTEGRATION_SETTINGS.ASSISTENT.TABS.HANDOFF'),
        },
        {
          key: 'internet_access',
          name: this.$t('INTEGRATION_SETTINGS.ASSISTENT.TABS.INTERNET_ACCESS'),
        },
        {
          key: 'macros',
          name: this.$t('INTEGRATION_SETTINGS.ASSISTENT.TABS.MACROS'),
        },
        {
          key: 'resolution_rules',
          name: this.$t('INTEGRATION_SETTINGS.ASSISTENT.TABS.RESOLUTION_RULES'),
        },
        {
          key: 'resume',
          name: this.$t('INTEGRATION_SETTINGS.ASSISTENT.TABS.RESUME'),
        },
      ];
    },
    mainTabs() {
      return this.tabs.slice(0, 8);
    },
    extraTabs() {
      return this.tabs.slice(8);
    },
    mainTabsIndex() {
      const idx = this.mainTabs.findIndex(t => t.key === this.selectedTabKey);
      return idx >= 0 ? idx : 0;
    },
    selectedTabKey() {
      return this.tabs[this.selectedTabIndex]?.key;
    },
  },
  watch: {
    $route(to) {
      if (to.name === 'virtual_assistant_show') {
        this.fetchAssistentSettings();
      }
    },
  },
  mounted() {
    this.fetchAssistentSettings();
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    fetchAssistentSettings() {
      this.selectedTabIndex = 0;
      this.isLoading = true;
      this.$store.dispatch('assistent/get', { version: 'v2' }).finally(() => {
        this.isLoading = false;
      });
    },
    onMainTabChange(mainIndex) {
      const mainTab = this.mainTabs[mainIndex];
      if (!mainTab) return;
      const overallIndex = this.tabs.findIndex(t => t.key === mainTab.key);
      if (overallIndex !== -1) {
        this.selectedTabIndex = overallIndex;
      }
    },
    toggleExtraMenu() {
      this.showExtraMenu = !this.showExtraMenu;
      if (this.showExtraMenu) {
        this.$nextTick(() => this.positionExtraMenu());
      }
    },
    positionExtraMenu() {
      const button = this.$refs.extraButton?.$el || this.$refs.extraButton;
      const menu = this.$refs.extraMenu;
      if (!button || !menu) return;

      const buttonRect = button.getBoundingClientRect();
      const wrapperRect = this.$refs.tabsWrapper.getBoundingClientRect();

      const left = buttonRect.left - wrapperRect.left;
      const top = buttonRect.bottom - wrapperRect.top + 4;

      this.extraMenuStyle = {
        position: 'absolute',
        left: `${left}px`,
        top: `${top}px`,
      };
    },
    handleClickOutside(event) {
      const wrapper = this.$refs.tabsWrapper;
      if (wrapper && !wrapper.contains(event.target)) {
        this.showExtraMenu = false;
      }
    },
    selectExtraTab(tab) {
      this.showExtraMenu = false;
      const index = this.tabs.findIndex(t => t.key === tab.key);
      if (index !== -1) {
        this.selectedTabIndex = index;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '~dashboard/assets/scss/variables';
@import '~dashboard/assets/scss/mixins';

.settings {
  background: $color-white;

  .settings--tabs {
    ::v-deep .tabs {
      padding: 0;
    }
  }

  .tabs {
    padding: 0;
    margin-bottom: -1px;
  }
}

.settings--content {
  padding-bottom: var(--space-medium);
  margin-bottom: var(--space-medium);
}

.settings--tabs-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
}

.extra-tabs-trigger {
  position: relative;
  cursor: pointer;
}

.extra-tabs__menu {
  position: absolute;
  background: #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  z-index: 100;
  list-style: none;
  padding: 0.5rem 0;
  min-width: 180px;
}

.extra-tabs__item {
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: var(--font-size-mini);
  white-space: nowrap;
}

.extra-tabs__item:hover {
  background: var(--b-100);
}
</style>
